import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useState } from 'react';
import type { BusinessUnit } from 'services/types/businessUnit';
import type { Client } from 'services/types/client';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW_CARD,
  STATUS_GOOD,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
} from 'styles/tokens';

import ClientTopicsEditor from './ClientTopicsEditor';

interface Props {
  client: Client;
  businessUnit?: BusinessUnit;
  onEdit?: () => void;
}

const formatRelativeTime = (input?: string | Date | null): string | null => {
  if (!input) return null;
  const then = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  if (diffMs < 0) return null;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'hace instantes';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} d`;
  return new Date(then).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatClientId = (id: string | number): string => {
  const str = String(id);
  return str.length > 8 ? `${str.slice(0, 4)}…${str.slice(-4)}` : str;
};

interface ContactRowProps {
  icon: React.ReactNode;
  value: string | null | undefined;
  emptyLabel: string;
  ariaLabel: string;
  onCopy: () => void;
  copied: boolean;
}

const ContactRow: React.FC<ContactRowProps> = ({ icon, value, emptyLabel, ariaLabel, onCopy, copied }) => {
  const hasValue = !!value;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: RADIUS_ITEM,
        transition: 'background-color 0.15s ease',
        '&:hover': { backgroundColor: SURFACE_HOVER },
        '&:hover .row-actions': { opacity: 1 },
      }}
      aria-label={ariaLabel}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', color: alpha(NAVY, 0.55), flexShrink: 0 }}>{icon}</Box>
      <Typography
        variant="body2"
        sx={{
          color: hasValue ? NAVY : alpha(NAVY, 0.4),
          fontStyle: hasValue ? 'normal' : 'italic',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {hasValue ? value : emptyLabel}
      </Typography>
      <Box
        className="row-actions"
        sx={{ display: 'flex', opacity: hasValue ? 0.55 : 0, transition: 'opacity 0.15s ease' }}
      >
        <Tooltip title={copied ? '¡Copiado!' : 'Copiar'} placement="top" arrow>
          <span>
            <IconButton
              size="small"
              onClick={onCopy}
              disabled={!hasValue}
              sx={{ color: copied ? STATUS_GOOD : alpha(NAVY, 0.7), '&:hover': { color: NAVY } }}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

const COPY_LABELS: Record<string, string> = {
  id: 'ID copiado',
  phone: 'Teléfono copiado',
  email: 'Email copiado',
  document: 'Documento copiado',
  whatsAppUsername: 'Link de WhatsApp copiado',
};

const ClientIdentityHero: React.FC<Props> = ({ client, businessUnit, onEdit }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { notifyCopied } = useFeedback();

  const fullName = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim();
  const documentValue = client.dni || client.rut;
  const documentLabel = client.dni ? 'DNI' : 'RUT';
  const relativeUpdate = formatRelativeTime(client.lastCRMUpdate);
  const absoluteUpdate = client.lastCRMUpdate
    ? new Date(client.lastCRMUpdate).toLocaleString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;

  const handleCopy = (field: string, value: string) => {
    if (!value) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedField(field);
        notifyCopied(COPY_LABELS[field]);
        setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 1500);
      })
      .catch((e) => {
        console.error('Error copying to clipboard:', e);
      });
  };

  return (
    <Box
      sx={{
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_CARD,
        boxShadow: SHADOW_CARD,
        p: 2.5,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: NAVY,
                fontSize: '1.1rem',
                lineHeight: 1.25,
                wordBreak: 'break-word',
              }}
            >
              {fullName || 'Sin nombre'}
            </Typography>
            {client.hasValidName && (
              <Tooltip title="Nombre validado" placement="top" arrow>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: STATUS_GOOD,
                    color: '#fff',
                  }}
                >
                  <CheckIcon sx={{ fontSize: 12 }} />
                </Box>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Editar cliente" placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={onEdit}
                  sx={{
                    ml: 'auto',
                    color: alpha(NAVY, 0.6),
                    '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: alpha(NAVY, 0.75),
              mt: 0.25,
              fontSize: '0.85rem',
            }}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>
              Origen:
            </Box>{' '}
            {client.source || '—'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: alpha(NAVY, 0.75),
              mt: 0.25,
              fontSize: '0.85rem',
            }}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>
              Última subida al CRM:
            </Box>{' '}
            {absoluteUpdate ? `${absoluteUpdate}${relativeUpdate ? ` (${relativeUpdate})` : ''}` : '—'}
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Tooltip title={copiedField === 'id' ? '¡Copiado!' : 'Copiar ID'} placement="top" arrow>
              <Box
                component="button"
                type="button"
                onClick={() => handleCopy('id', String(client.id))}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  border: 'none',
                  borderRadius: RADIUS_ITEM,
                  backgroundColor: copiedField === 'id' ? alpha(STATUS_GOOD, 0.12) : SURFACE_HOVER,
                  color: copiedField === 'id' ? STATUS_GOOD : alpha(NAVY, 0.75),
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  '&:hover': {
                    backgroundColor: copiedField === 'id' ? alpha(STATUS_GOOD, 0.16) : alpha(NAVY, 0.1),
                    color: copiedField === 'id' ? STATUS_GOOD : NAVY,
                  },
                }}
              >
                ID {formatClientId(client.id)}
                {copiedField === 'id' ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
              </Box>
            </Tooltip>
            {businessUnit?.name && (
              <Typography
                variant="caption"
                sx={{ color: alpha(NAVY, 0.65), fontSize: '0.8rem', fontFamily: 'monospace' }}
              >
                · {businessUnit.name}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <ContactRow
          icon={<PhoneIcon fontSize="small" />}
          value={client.phone}
          emptyLabel="Sin teléfono"
          ariaLabel="Teléfono"
          onCopy={() => handleCopy('phone', client.phone ?? '')}
          copied={copiedField === 'phone'}
        />
        <ContactRow
          icon={<AlternateEmailIcon fontSize="small" />}
          value={client.whatsAppUsername}
          emptyLabel="Sin usuario de WhatsApp"
          ariaLabel="Usuario de WhatsApp"
          onCopy={() =>
            handleCopy(
              'whatsAppUsername',
              client.whatsAppUsername
                ? `https://wa.me/${client.whatsAppUsername.startsWith('@') ? '' : '@'}${client.whatsAppUsername}`
                : '',
            )
          }
          copied={copiedField === 'whatsAppUsername'}
        />
        <ContactRow
          icon={<EmailIcon fontSize="small" />}
          value={client.email}
          emptyLabel="Sin email"
          ariaLabel="Email"
          onCopy={() => handleCopy('email', client.email ?? '')}
          copied={copiedField === 'email'}
        />
        <ContactRow
          icon={<BadgeIcon fontSize="small" />}
          value={documentValue ? `${documentLabel}: ${documentValue}` : null}
          emptyLabel={`Sin ${documentLabel}`}
          ariaLabel={documentLabel}
          onCopy={() => handleCopy('document', documentValue ?? '')}
          copied={copiedField === 'document'}
        />
      </Box>

      <Grid container spacing={0.5} sx={{ mt: 1.5 }}>
        <Grid size={12}>
          <Typography
            variant="caption"
            component="div"
            sx={{
              color: alpha(NAVY, 0.5),
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.65rem',
              mb: 0.5,
            }}
          >
            Tags
          </Typography>
          <ClientTopicsEditor client={client} businessUnit={businessUnit} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientIdentityHero;
